import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're subscribed. Welcome to Femora Life.");
      setEmail("");
    } catch (err) {
      const msg =
        err?.response?.status === 422
          ? "Please enter a valid email address."
          : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-ink text-white">
      {/* Newsletter */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 grid md:grid-cols-2 gap-8 md:items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight">Join the Femora Life letter</h2>
            <p className="text-white/60 mt-2 text-sm md:text-base">
              Evidence-based women's wellness, new formulas and launch news — straight to your inbox.
            </p>
          </div>
          <form onSubmit={subscribe} data-testid="newsletter-form" className="flex gap-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              data-testid="newsletter-email-input"
              className="flex-1 rounded-md bg-white/5 border border-white/20 px-4 py-3 text-white placeholder:text-white/40 outline-none focus:border-white/60 transition-colors duration-200"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit"
              className="rounded-md bg-white text-ink font-semibold px-6 py-3 hover:bg-rose hover:text-white transition-colors duration-200 disabled:opacity-50 inline-flex items-center gap-2"
            >
              Subscribe <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </div>

      {/* Links */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <span className="text-xl font-bold tracking-[0.12em]">FEMORA LIFE</span>
          <p className="text-white/50 text-sm mt-3 max-w-xs">{BRAND.tagline} — science-backed wellness for every stage of a woman's life.</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-white/40 mb-4">Explore</p>
          <ul className="space-y-3 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.label}>
                <a href={l.href} className="text-white/75 hover:text-white transition-colors duration-200">{l.label}</a>
              </li>
            ))}
            <li>
              <Link to="/products" className="text-white/75 hover:text-white transition-colors duration-200">All Products</Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-white/40 mb-4">Company</p>
          <ul className="space-y-3 text-sm">
            <li><Link to="/contact" className="text-white/75 hover:text-white transition-colors duration-200">Contact</Link></li>
            <li className="text-white/75">Global · Est. 2025</li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-white/40 mb-4">Get in touch</p>
          <ul className="space-y-3 text-sm">
            <li><a href="mailto:hello@femora.life" className="text-white/75 hover:text-white transition-colors duration-200">hello@femora.life</a></li>
            <li><a href="tel:+918019369369" className="text-white/75 hover:text-white transition-colors duration-200">+91 80193 69369</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-white/40">© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</p>
          <p className="text-[0.65rem] text-white/35 max-w-lg md:text-right">
            These statements have not been evaluated by any medical authority. Not intended to diagnose, treat, cure or prevent any disease.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
